/**
 * Zod schemas — the single source of truth for every API shape iFlow consumes.
 * All TypeScript types are derived via `z.infer` (see ./index.ts); we never
 * hand-duplicate a type. Responses are parsed through these at the client edge
 * so a backend contract drift surfaces as a typed error, not a silent `any`.
 */
import { z } from 'zod';

/* ------------------------------------------------------------------ shared */

export const OutcomeSchema = z.enum(['HIT', 'FAIL']);
export const HealthStateSchema = z.enum(['healthy', 'degraded', 'down']);
export const CircuitStateSchema = z.enum(['closed', 'open', 'half-open']);
export const ErrorCodeSchema = z.enum([
  'unknownModel',
  'validationError',
  'authFailure',
  'modelTimeout',
  'upstreamError',
  'accountDisabled',
  'modelNotPermitted',
  'insufficientBalance',
]);
export const TimeWindowSchema = z.enum(['5m', '1h', '24h', '7d']);

/* -------------------------------------------------------------------- auth */

export const UserSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().email(),
  role: z.string(),
});

export const LoginResponseSchema = z.object({
  token: z.string(),
  refreshToken: z.string(),
  expiresIn: z.number(),
  user: UserSchema,
  /** True on a client's first login — they must set a new password. */
  mustChangePassword: z.boolean().optional().default(false),
});

export const RefreshResponseSchema = z.object({
  token: z.string(),
  expiresIn: z.number(),
});

/** Login captcha challenge: opaque id + an SVG image to display. */
export const CaptchaSchema = z.object({
  id: z.string(),
  svg: z.string(),
});

export const LoginStatsSchema = z.object({
  uniqueUsers: z.number(),
  totalLogins: z.number(),
  recent: z.array(
    z.object({
      userId: z.string(),
      username: z.string(),
      email: z.string(),
      ts: z.string(),
    }),
  ),
});

/* ------------------------------------------------------------------ clients / billing */

export const ClientSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().nullable(),
  company: z.string().nullable(),
  apiKeyPreview: z.string(),
  /** Raw key — present ONLY in create / regenerate responses, shown once. */
  apiKey: z.string().optional(),
  /** First-time password — present ONLY in the create response, shown once. */
  tempPassword: z.string().optional(),
  active: z.boolean(),
  balance: z.number(),
  allowedModels: z.array(z.string()),
  /** Assigned-but-switched-off models — access blocked, assignment kept. */
  disabledModels: z.array(z.string()).optional().default([]),
  planLabel: z.string().nullable(),
  planType: z.enum(['prepaid', 'postpaid']).optional().default('prepaid'),
  gstNumber: z.string().nullable(),
  createdAt: z.string(),
  totalCalls: z.number().optional().default(0),
  totalHits: z.number().optional().default(0),
  creditsSpent: z.number().optional().default(0),
});

export const ClientListSchema = z.object({
  clients: z.array(ClientSchema),
});

export const BillSchema = z.object({
  id: z.string(),
  clientId: z.string(),
  filename: z.string(),
  originalName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number(),
  label: z.string().nullable(),
  uploadedBy: z.string().nullable(),
  createdAt: z.number(),
});

export const BillListSchema = z.object({
  bills: z.array(BillSchema),
});

export const TransactionSchema = z.object({
  id: z.number(),
  clientId: z.string(),
  ts: z.string(),
  type: z.enum(['recharge', 'deduction']),
  amount: z.number(),
  balanceBefore: z.number(),
  balanceAfter: z.number(),
  note: z.string().nullable(),
  requestId: z.string().nullable(),
});

export const TransactionPageSchema = z.object({
  items: z.array(TransactionSchema),
  nextCursor: z.string().nullable(),
});

export const PricingRowSchema = z.object({
  modelKey: z.string(),
  cost: z.number(),
});

export const PricingListSchema = z.object({
  pricing: z.array(PricingRowSchema),
});

// ---- provider credentials (super-admin managed upstream vendor config) ----
export const ProviderCredentialSchema = z.object({
  id: z.string(),
  providerId: z.string(),
  key: z.string(),
  value: z.string().nullable(),
  secret: z.boolean(),
  createdAt: z.number(),
  updatedAt: z.number(),
});

export const ProviderSchema = z.object({
  id: z.string(),
  name: z.string(),
  label: z.string().nullable(),
  active: z.boolean(),
  createdAt: z.number(),
  updatedAt: z.number(),
  credentials: z.array(ProviderCredentialSchema),
});

export const ProviderListSchema = z.object({
  providers: z.array(ProviderSchema),
});

export const ClientBalanceSchema = z.object({
  balance: z.number(),
  active: z.boolean(),
  lastRechargeAt: z.string().nullable(),
});

/** Per-client usage broken down by model + this-month / all-time roll-ups. */
export const ClientUsageRowSchema = z.object({
  modelKey: z.string(),
  name: z.string(),
  calls: z.number(),
  hits: z.number(),
  fails: z.number(),
  pricePerHit: z.number(),
  creditsSpent: z.number(),
  lastCalledAt: z.string().nullable(),
});

const UsageRollupSchema = z.object({
  calls: z.number(),
  hits: z.number(),
  creditsSpent: z.number(),
});

export const ClientUsageSchema = z.object({
  byModel: z.array(ClientUsageRowSchema),
  month: UsageRollupSchema,
  allTime: UsageRollupSchema,
});

/** Model catalogue with per-hit price — for the "assign models" picker. */
export const CatalogModelSchema = z.object({
  key: z.string(),
  description: z.string(),
  type: z.enum(['score', 'ensemblescore', 'verify', 'document']),
  category: z.string().optional().default('Uncategorized'),
  upstreamUrl: z.string().nullable().optional(),
  pricePerHit: z.number(),
  /** DB-backed model fields (absent for verification services). */
  modelId: z.string().optional(),
  endpoint: z.string().optional(),
  active: z.boolean().optional().default(true),
  requestSchema: z.string().nullable().optional(),
  /** Verify services: the mandatory `data` fields (rest of the sample is optional). */
  required: z.array(z.string()).nullable().optional(),
  /** Short badge on the product card, e.g. "Latest" or "V2". */
  label: z.string().nullable().optional(),
  /** Present only on the per-client catalogue (GET /admin/clients/:id/catalog). */
  overridePrice: z.number().nullable().optional(),
  effectivePrice: z.number().optional(),
});

export const CatalogListSchema = z.object({
  models: z.array(CatalogModelSchema),
});

/** Per-client catalogue: catalogue + this client's assigned models. */
export const ClientCatalogSchema = z.object({
  models: z.array(CatalogModelSchema),
  allowedModels: z.array(z.string()),
});

/** Client-portal catalogue: each model flagged with the client's subscription. */
export const MyCatalogModelSchema = z.object({
  key: z.string(),
  description: z.string(),
  category: z.string().optional().default('Uncategorized'),
  type: z.enum(['score', 'ensemblescore', 'verify', 'document']),
  subscribed: z.boolean(),
  /** Assigned but switched off by the admin — shown as "Disabled". */
  disabled: z.boolean().optional().default(false),
  pricePerHit: z.number(),
  effectivePrice: z.number(),
  requestSchema: z.string().nullable().optional(),
  /** Verify services: the mandatory `data` fields (rest of the sample is optional). */
  required: z.array(z.string()).nullable().optional(),
  /** Short badge on the product card, e.g. "Latest" or "V2". */
  label: z.string().nullable().optional(),
});

export const MyCatalogSchema = z.object({
  models: z.array(MyCatalogModelSchema),
  subscribedToAll: z.boolean(),
});

/** A client's request for access to a model (notify-only workflow). */
export const ModelRequestSchema = z.object({
  id: z.string(),
  clientId: z.string(),
  clientName: z.string().nullable(),
  clientEmail: z.string().nullable(),
  modelKey: z.string(),
  modelDescription: z.string(),
  note: z.string().nullable(),
  status: z.enum(['pending', 'approved', 'rejected']),
  alreadyAssigned: z.boolean().optional().default(false),
  createdAt: z.string(),
  decidedAt: z.string().nullable(),
});

export const ModelRequestListSchema = z.object({
  requests: z.array(ModelRequestSchema),
  pendingCount: z.number().optional().default(0),
});

/** Monthly billing report for one client (per-model totals + grand total). */
export const BillingLineSchema = z.object({
  modelKey: z.string(),
  name: z.string(),
  calls: z.number(),
  hits: z.number(),
  pricePerHit: z.number(),
  amount: z.number(),
});

/** One day of the bill: that day's totals + per-model hits/amount (YYYY-MM-DD). */
export const BillingDaySchema = z.object({
  date: z.string(),
  hits: z.number(),
  amount: z.number(),
  lines: z.array(
    z.object({ modelKey: z.string(), name: z.string(), hits: z.number(), amount: z.number() }),
  ),
});

export const BillingReportSchema = z.object({
  client: z.object({
    id: z.string(),
    name: z.string(),
    planType: z.enum(['prepaid', 'postpaid']).optional().default('prepaid'),
  }),
  clientId: z.string(),
  year: z.number(),
  month: z.number(),
  /** Effective period, inclusive (YYYY-MM-DD). */
  from: z.string().optional(),
  to: z.string().optional(),
  lines: z.array(BillingLineSchema),
  days: z.array(BillingDaySchema).optional().default([]),
  grandTotal: z.number(),
  totalHits: z.number(),
});

// Months + models a client actually has data for — drives the filter dropdowns
// on the client-detail tabs (DB-derived, never hardcoded).
export const ClientFilterOptionsSchema = z.object({
  periods: z.array(z.object({ year: z.number(), month: z.number() })),
  models: z.array(z.object({ key: z.string(), name: z.string() })),
});

/* ---------------------------------------------------------------- overview */

export const SummarySchema = z.object({
  totalRequests: z.number(),
  hits: z.number(),
  fails: z.number(),
  successRate: z.number(),
  avgLatencyMs: z.number(),
  p95LatencyMs: z.number(),
  reqPerMin: z.number(),
  deltaVsPrev: z.object({
    requests: z.number(),
    successRate: z.number(),
  }),
});

export const TimeseriesSchema = z.object({
  bucketSec: z.number(),
  points: z.array(
    z.object({
      ts: z.string(),
      hits: z.number(),
      fails: z.number(),
    }),
  ),
});

export const StatusCodesSchema = z.object({
  buckets: z.array(z.object({ code: z.string(), count: z.number() })),
});

export const ErrorCodesSchema = z.object({
  errors: z.array(z.object({ code: ErrorCodeSchema, count: z.number() })),
});

export const LatencyHistogramSchema = z.object({
  bins: z.array(z.object({ from: z.number(), to: z.number(), count: z.number() })),
  markers: z.object({ p50: z.number(), p90: z.number(), p95: z.number(), p99: z.number() }),
});

/* ------------------------------------------------------------------ models */

export const ModelRowSchema = z.object({
  modelId: z.string(),
  /** Catalogue key (same as modelId for rows) — used by the docs component. */
  key: z.string().optional(),
  name: z.string(),
  /** 'verify' | 'document' | scoring endpoint — the docs component branches on this. */
  type: z.string().nullable().optional(),
  calls: z.number(),
  hits: z.number(),
  fails: z.number(),
  hitRate: z.number(),
  failRate: z.number(),
  avgLatencyMs: z.number(),
  p95LatencyMs: z.number(),
  lastCalledAt: z.string().nullable(),
  health: HealthStateSchema,
  sparkline: z.array(z.number()),
  requestSchema: z.string().nullable().optional(),
  /** Verify services: the mandatory `data` fields (rest of the sample is optional). */
  required: z.array(z.string()).nullable().optional(),
});

export const ModelsResponseSchema = z.object({ models: z.array(ModelRowSchema) });

export const RequestSummarySchema = z.object({
  requestId: z.string(),
  ts: z.string(),
  modelId: z.string(),
  outcome: OutcomeSchema,
  httpStatus: z.number(),
  errorCode: ErrorCodeSchema.nullable(),
  latencyMs: z.number(),
  creditsDeducted: z.number().optional().default(0),
});

export const ModelDetailSchema = z.object({
  model: ModelRowSchema,
  timeseries: z.array(z.object({ ts: z.string(), hits: z.number(), fails: z.number() })),
  latency: z.array(z.object({ ts: z.string(), avg: z.number(), p95: z.number() })),
  failureReasons: z.array(z.object({ code: ErrorCodeSchema, count: z.number() })),
  recentCalls: z.array(RequestSummarySchema),
});

/* ---------------------------------------------------------------- requests */

export const RequestsPageSchema = z.object({
  items: z.array(RequestSummarySchema),
  nextCursor: z.string().nullable(),
});

export const RequestDetailSchema = RequestSummarySchema.extend({
  request: z.record(z.unknown()),
  response: z.record(z.unknown()),
  validationErrors: z.array(z.object({ field: z.string(), message: z.string() })),
  latencyBreakdown: z.object({
    queueMs: z.number(),
    inferenceMs: z.number(),
    upstreamMs: z.number(),
    totalMs: z.number(),
  }),
});

/* ------------------------------------------------------------------ health */

export const HealthSchema = z.object({
  status: z.enum(['ok', 'degraded', 'down']),
  uptimeSec: z.number(),
  version: z.string(),
});

export const ReadySchema = z.object({
  ready: z.boolean(),
  checks: z.array(z.object({ name: z.string(), ok: z.boolean() })),
});

export const SystemSchema = z.object({
  inFlight: z.number(),
  maxInFlight: z.number(),
  circuitBreakers: z.array(z.object({ modelId: z.string(), state: CircuitStateSchema })),
  rateLimit: z.object({ used: z.number(), limit: z.number(), windowSec: z.number() }),
});

/* --------------------------------------------------------------- envelope */

/** Backend envelope (mirrors bre-app's ApiResponse<T>): { success, data, message }. */
export function envelope<T extends z.ZodTypeAny>(data: T) {
  return z.object({ success: z.literal(true), data, message: z.string().optional() });
}
